import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import { Box, Typography } from '@mui/material';
import { Checkbox, Option } from 'components/ui/FormControls';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useNotification } from '../../providers/NotificationProvider';
import { sanitizeRouteId } from '../../../utils/route-params.utils';
import { useUser } from '../../providers/UserProvider';
import {
    FormSnackBarType,
    generateSnackBarFormMessage,
    useFormControl,
    useFormGroup,
} from '../../../utils/form.utils';
import {
    ArticleValidationError,
    dateValidator,
    requiredValidator,
} from '../../../utils/validator.utils';
import {
    ADD_ARTICLES,
    ALL_STATIC,
    ARCHIVE_ARTICLE,
    EDIT_ARTICLE,
    GET_ARTICLE,
    GET_SUBSCRIBERS_COUNT,
    GET_TEMPLATE,
    PUBLISH_ADD,
    PUBLISH_EDIT,
} from '../../../gql/articles';
import { DESKS_SHORT } from '../../../gql/desks';
import { TABLEAU_ENABLED } from '../../../utils/tableau';
import {
    getCategoriesAsList,
    prepareStaticDataList,
} from '../../../utils/list-utils';
import { convertToDate } from '../../../utils/date.utils';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import {
    getContributorRoleUsers,
    getUsersWithNames,
} from '../../../utils/filtering.utils';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { translateErrorMessage } from '../../../shared/error-handling';
import {
    getPromotionalContentIdsFromHtml,
    htmlToPromotionalContentShortcodes,
    promotionalContentShortcodesToHtml,
} from '../../../utils/promotional-content.utils';
import {
    expandSelectedUserGuideScopeValuesToStoredScopes,
    getAvailableUserGuideScopeOptions,
    mapStoredUserGuideScopesToSelectableValues,
} from '../../../utils/user-guide.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { USER_PUBLISH_CHANNELS } from '../../../gql/users';
import { GET_CHANNELS_ADD_EDIT_ARTICLE } from '../../../gql/channels';
import { usersContains, isStringArray } from '../../../utils/utils';

import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import AddEditHeader from '../../ui/admin/AddEditHeader';
import ArticleEditor from '../../ui/admin/ArticleEditor';
import ArticleSideBar from '../../ui/admin/ArticleSideBar';
import {
    ArticleChangeHookType,
    ArticleEditError,
    ArticleState,
    ResearchRole,
} from '../../../shared/global-enums';
import {
    AlertMessage,
    Article,
    ArticleInfo,
    ArticleUpsertDto,
    CategoryItemNode,
    Channel,
    ChannelOwner,
    ChannelTypes,
    Desk,
    IdAndName,
    PromotionalContentSummary,
    Template,
    User,
} from '../../../shared/types';
import { AlertBox } from 'components/ui/Feedback';
import {
    clearArticleDraftStorage,
    useArticleDraftAutoSave,
} from './hooks/useArticleDraftAutoSave';

type UserPublishChannelsData = {
    me?: { channelsToPublish?: Channel[] };
};

type AllChannelsData = {
    channels?: Channel[];
};

const returnListUrl = baseUrls.publisher.articles.list;

// localStorage key broadcast (via the `storage` event) by a tab that just
// created a brand-new article, so sibling tabs with the same unsaved "add
// new article" form open can lock themselves and avoid creating a duplicate.
const NEW_ARTICLE_CREATED_KEY = 'newArticleCreatedSignal';

enum DialogType {
    UpdateArticleWithoutSubscribers,
    UpdateArticle,
    PublishArticleWithoutSubscribers,
    PublishArticle,
    PublishArticleWithBackDateAndWithoutSubscribers,
    PublishArticleWithBackDate,
}

interface ErrorPair {
    value: string | any;
    kind: ArticleEditError;
}

interface DialogLabel {
    notification: string;
    confirmation: string;
}

const getLabelAccordingToDialogType = (dialogType: DialogType): DialogLabel => {
    switch (dialogType) {
        case DialogType.PublishArticle:
            return {
                notification: ' All subscribers will be notified.',
                confirmation: ' Do you want to proceed?',
            };
        case DialogType.PublishArticleWithBackDate:
            return {
                notification: ' This article will be available to all readers.',
                confirmation:
                    ' Before publishing, check below if you want to notify all subscribers.',
            };
        case DialogType.UpdateArticle:
            return {
                notification: '',
                confirmation:
                    ' Before submitting it, check below if you want to notify all the subscribers of this update.',
            };
        case DialogType.PublishArticleWithoutSubscribers:
        case DialogType.UpdateArticleWithoutSubscribers:
        case DialogType.PublishArticleWithBackDateAndWithoutSubscribers:
            return {
                notification: '',
                confirmation: ' Do you want to proceed?',
            };

        default:
            return {
                notification: '',
                confirmation: '',
            };
    }
};

const getDialogType = (
    action: string,
    numberOfSubscribers: number,
    withCustomPublishDate: boolean
): DialogType => {
    if (
        action === 'Publish' &&
        numberOfSubscribers > 0 &&
        !withCustomPublishDate
    )
        return DialogType.PublishArticle;

    if (
        action === 'Publish' &&
        numberOfSubscribers > 0 &&
        withCustomPublishDate
    )
        return DialogType.PublishArticleWithBackDate;

    if (
        action === 'Publish' &&
        numberOfSubscribers === 0 &&
        !withCustomPublishDate
    )
        return DialogType.PublishArticleWithoutSubscribers;

    if (
        action === 'Publish' &&
        numberOfSubscribers === 0 &&
        withCustomPublishDate
    )
        return DialogType.PublishArticleWithBackDateAndWithoutSubscribers;

    if (action !== 'Publish' && numberOfSubscribers > 0)
        return DialogType.UpdateArticle;

    if (action !== 'Publish' && numberOfSubscribers === 0)
        return DialogType.UpdateArticleWithoutSubscribers;

    return DialogType.PublishArticle;
};

const formatIsPremiumValue = (value: boolean | undefined) => {
    if (typeof value === undefined) return undefined;

    return !value;
};

export const ArticleAddEdit = () => {
    const { _id: routeId, templateId: routeTemplateId } = useParams<{
        _id?: string;
        templateId?: string;
    }>();
    const _id = sanitizeRouteId(routeId);
    const templateId = sanitizeRouteId(routeTemplateId);
    const history = useHistory();
    const { userDetails, hasScope } = useUser();
    const isAdmin = [ResearchRole.Administrator].some(hasScope);
    const { data: tableauData } = useQuery(TABLEAU_ENABLED, {
        fetchPolicy: 'network-only',
    });
    const userHasTableuAccess = tableauData?.me?.tableauAccess ?? undefined;

    const publisherFormControl = useFormControl<string>('');
    const contributorsFormControl = useFormControl<string[]>([]);
    const detailSummaryFormControl = useFormControl<string>('');
    const categoriesFormControl = useFormControl<string[]>([]);
    const tagsFormControl = useFormControl<string[]>([]);
    const publishDateFormControl = useFormControl<Date | undefined>(undefined);
    const articleTextFormControl = useFormControl<string | undefined>(
        undefined
    );
    const articleTitleFormControl = useFormControl<string>('', [
        requiredValidator,
    ]);
    const openToAllFormControl = useFormControl<boolean>(false, []);
    const expiryOffsetFormControl = useFormControl<string>('');
    const channelFormControl = useFormControl<string>('', []);
    const showWatermarkFormControl = useFormControl<boolean>(false, []);
    const displayInChartSectionFormControl = useFormControl<boolean>(false, []);
    const isPinnedFormControl = useFormControl<boolean>(false, []);
    const isTimelessFormControl = useFormControl<boolean>(false, []);
    const isUserGuideArticleFormControl = useFormControl<boolean>(false, []);
    const userGuideScopesFormControl = useFormControl<string[]>([]);

    const setInitialFormValues = (
        values: Article | Template | ArticleUpsertDto
    ) => {
        articleTitleFormControl.setInitialValue(values.title);
        articleTextFormControl.setInitialValue(values.html || '');
        publisherFormControl.setInitialValue(values.publishedBy?._id);
        detailSummaryFormControl.setInitialValue(
            'summary' in values ? values.summary : ''
        );
        publishDateFormControl.setInitialValue(
            'publishDate' in values ? values.publishDate : ''
        );
        openToAllFormControl.setInitialValue(
            'isPremium' in values
                ? formatIsPremiumValue(values.isPremium)
                : undefined
        );
        expiryOffsetFormControl.setInitialValue(
            'premiumExpiryOffset' in values ? values.premiumExpiryOffset : ''
        );
        channelFormControl.setInitialValue(
            'premiumChannel' in values ? values.premiumChannel : ''
        );
        contributorsFormControl.setInitialValue(
            isStringArray(values.contributors)
                ? values.contributors
                : ((values.contributors || []) as Partial<User>[]).map(
                      (c) => c._id
                  )
        );
        categoriesFormControl.setInitialValue(
            isStringArray(values.categories)
                ? values.categories
                : ((values.categories || []) as IdAndName[]).map((c) => c._id)
        );
        tagsFormControl.setInitialValue(
            isStringArray(values.tags)
                ? values.tags
                : ((values.tags || []) as IdAndName[]).map((c) => c._id)
        );
        showWatermarkFormControl.setInitialValue(
            'showWatermark' in values ? values.showWatermark : false
        );
        displayInChartSectionFormControl.setInitialValue(
            'displayInChartSection' in values
                ? values.displayInChartSection
                : false
        );
        isPinnedFormControl.setInitialValue(
            'isPinned' in values ? values.isPinned : false
        );
        isTimelessFormControl.setInitialValue(
            'isTimeless' in values ? values.isTimeless : false
        );
        isUserGuideArticleFormControl.setInitialValue(
            'isUserGuideArticle' in values ? values.isUserGuideArticle : false
        );
        userGuideScopesFormControl.setInitialValue(
            mapStoredUserGuideScopesToSelectableValues(
                'userGuideScopes' in values ? values.userGuideScopes : []
            )
        );
    };

    const [getChannels, { loading: channelsLoading, data: channelsData }] =
        useLazyQuery<UserPublishChannelsData>(USER_PUBLISH_CHANNELS, {
            fetchPolicy: 'network-only',
        });

    const { loading: desksLoading, data: desksData } = useQuery(DESKS_SHORT, {
        fetchPolicy: 'network-only',
        variables: {
            getOnlyUsersDesks: true,
        },
    });
    const { loading: allChannelsLoading, data: allChannelsData } =
        useQuery<AllChannelsData>(GET_CHANNELS_ADD_EDIT_ARTICLE, {
            fetchPolicy: 'network-only',
        });

    const { data, loading } = useQuery(GET_ARTICLE, {
        skip: !!templateId || !_id,
        variables: { id: _id },
        fetchPolicy: 'network-only',
    });

    const templateQuery = useQuery(GET_TEMPLATE, {
        skip: !templateId,
        variables: { id: templateId },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const template: Template | undefined = data?.document ?? undefined;
            if (!template) return;

            setInitialFormValues(template);

            const currentDate = moment().format('DD MMM YYYY');
            const formattedTitle = template.title.replace(
                /{{Today}}/g,
                currentDate
            );
            articleTitleFormControl.setInitialValue(formattedTitle);
        },
    });

    const { data: staticData, loading: staticLoading } = useQuery(ALL_STATIC, {
        fetchPolicy: 'network-only',
    });

    const querySubscribers = useQuery(GET_SUBSCRIBERS_COUNT, {
        fetchPolicy: 'network-only',
        variables: {
            channelId: channelFormControl.value
                ? channelFormControl.value
                : undefined,
        },
    });

    const { showMessage } = useNotification();
    const [addDbAction] = useMutation(ADD_ARTICLES);
    const [editDbAction] = useMutation(EDIT_ARTICLE);
    const [archiveDbAction] = useMutation(ARCHIVE_ARTICLE);
    const [publishAddDbAction] = useMutation(PUBLISH_ADD);
    const [publishEditDbAction] = useMutation(PUBLISH_EDIT);

    const [formBusy, setFormBusy] = useState<boolean>(false);
    // Guards against creating duplicate articles when the same "Add new
    // article" form is open in more than one browser tab. Once any tab
    // successfully creates the article (save-as-draft or publish), it
    // broadcasts NEW_ARTICLE_CREATED_KEY via localStorage. Any other tab
    // still showing the same unsaved "new" form picks this up through the
    // `storage` event and locks itself to prevent a second, separate
    // article being created from the same content.
    const [duplicateTabBlocked, setDuplicateTabBlocked] =
        useState<boolean>(false);
    const [confirmationDialogOpened, setConfirmationDialogOpened] =
        useState<boolean>(false);
    const [publishDialogOpened, setPublishDialogOpened] =
        useState<boolean>(false);
    const [pageMessage, setPageMessage] = useState<AlertMessage>({
        style: 'info',
        message: null,
    });

    const form = useFormGroup({
        publisher: publisherFormControl,
        contributors: contributorsFormControl,
        detailSummary: detailSummaryFormControl,
        categories: categoriesFormControl,
        tags: tagsFormControl,
        articleText: articleTextFormControl,
        articleTitle: articleTitleFormControl,
        publishDate: publishDateFormControl,
        openToAll: openToAllFormControl,
        expiryOffset: expiryOffsetFormControl,
        channel: channelFormControl,
        showWatermark: showWatermarkFormControl,
        displayInChartSection: displayInChartSectionFormControl,
        isPinned: isPinnedFormControl,
        isTimeless: isTimelessFormControl,
        isUserGuideArticle: isUserGuideArticleFormControl,
        userGuideScopes: userGuideScopesFormControl,
    });

    const editMode = !!data?.document;

    const articleInfo: ArticleInfo = {
        articleState: editMode ? data?.document.state : undefined,
        createDate: convertToDate(data?.document?.created),
        updateDate: new Date(data?.document?.lastEdited),
    };

    const [listUsers, setListUsers] = useState<User[]>([]);
    const listTags = prepareStaticDataList(staticData?.tags);
    const listPromotionalContent: PromotionalContentSummary[] = [
        ...(staticData?.promotionalContent ?? []),
    ];
    const preparedChannelsList = useMemo(
        () =>
            isAdmin
                ? [...(allChannelsData?.channels || [])]
                : [...(channelsData?.me?.channelsToPublish || [])],
        [
            isAdmin,
            allChannelsData?.channels,
            channelsData?.me?.channelsToPublish,
        ]
    );
    const availableUserGuideScopeOptions: Option[] = useMemo(
        () =>
            getAvailableUserGuideScopeOptions([], staticData?.userGuideScopes),
        [staticData]
    );

    const selectedChannel: Channel | undefined = (
        preparedChannelsList || []
    ).find((x) => x._id === form.channel.value);

    const wasPublishedPreviously =
        editMode && data.document.wasPublishedPreviously;

    const goBack = () => {
        history.push(returnListUrl);
    };

    const onChangeSidebarValue = (
        changeType: ArticleChangeHookType,
        value: string | string[] | boolean
    ) => {
        switch (changeType) {
            case ArticleChangeHookType.DetailsPublished:
                publisherFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsContributor:
                contributorsFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsSummary:
                detailSummaryFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsPublishDate:
                publishDateFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsOpenToAll:
                openToAllFormControl.setValue(value);
                expiryOffsetFormControl.setValue(undefined);
                break;
            case ArticleChangeHookType.DetailsShowWatermark:
                showWatermarkFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsDisplayInChartSection:
                displayInChartSectionFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsIsPinned:
                isPinnedFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsIsTimeless:
                isTimelessFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsUserGuideArticle:
                isUserGuideArticleFormControl.setValue(value);
                if (value) {
                    channelFormControl.setValue('');
                    openToAllFormControl.setValue(false);
                    expiryOffsetFormControl.setValue('');
                    publisherFormControl.setValue(userDetails?.id || '');
                }
                if (!value) {
                    userGuideScopesFormControl.setValue([]);
                }
                break;
            case ArticleChangeHookType.DetailsUserGuideScopes:
                userGuideScopesFormControl.setValue(value);
                break;
            case ArticleChangeHookType.DetailsChannel:
                channelFormControl.setValue(value);
                openToAllFormControl.setValue(undefined);
                expiryOffsetFormControl.setValue('');
                contributorsFormControl.setValue([]);
                publisherFormControl.setValue(
                    preparedChannelsList.find((c: Channel) => c._id === value)
                        ?.ownerId
                );
                break;
            case ArticleChangeHookType.DetailsPremiumOffset:
                expiryOffsetFormControl.setValue(value);
                break;
            case ArticleChangeHookType.TaxonomySection:
                categoriesFormControl.setValue([]);
                break;
            default:
                throw new Error(`UpdateType ${changeType} is not supported`);
        }
    };

    const onChangeMultiValue = (
        changeType: ArticleChangeHookType,
        value: Option[]
    ) => {
        const newValues = (value || []).map((option) =>
            option.value.toString()
        );

        switch (changeType) {
            case ArticleChangeHookType.TaxonomyCategories:
                categoriesFormControl.setValue(newValues);
                break;
            case ArticleChangeHookType.TaxonomyTags:
                tagsFormControl.setValue(newValues);
                break;
            default:
                throw new Error(`UpdateType ${changeType} is not supported`);
        }
    };

    const setBusy = (busy: boolean) => {
        // Set synchronously (no delay) so the Save/Publish/Archive buttons
        // are disabled immediately. A previous delayed version left a window
        // where rapid/duplicate clicks (e.g. Save then Publish) could both
        // fire before the buttons became disabled, creating duplicate
        // articles (one draft, one published) from the same edit session.
        setFormBusy(busy);
    };

    const onSubmit = async (event: any) => {
        changeValidators(false);
        event.preventDefault();
        form.setDirty();

        if (form.getIsValid()) {
            await saveArticle(generateArticleDto());
        }
    };

    const validateBeforePublish = (): ArticleValidationError[] => {
        const fieldsToCheck: ErrorPair[] = [
            { value: form.articleText, kind: ArticleEditError.TextAbsent },
            {
                value: form.contributors,
                kind: ArticleEditError.ContributorAbsent,
            },
            { value: form.detailSummary, kind: ArticleEditError.SummaryAbsent },
            { value: form.categories, kind: ArticleEditError.CategoriesAbsent },
            { value: form.articleTitle, kind: ArticleEditError.TitleAbsent },
        ];

        if (!form.isUserGuideArticle.value) {
            fieldsToCheck.push(
                {
                    value: form.publisher,
                    kind: ArticleEditError.PublisherAbsent,
                },
                { value: form.channel, kind: ArticleEditError.ChannelAbsent }
            );
        }

        return fieldsToCheck.map((item) => ({
            errorType: item.kind,
            message: item.value.validationMessage,
        }));
    };

    const handleValidation = () => {
        form.articleTitle.setDirty(true);
        changeValidators(true);
    };

    const onPublish = async (withEmail?: boolean) => {
        handleValidation();
        if (form.getIsValid()) {
            await publishArticle(generateArticleDto(withEmail));
        }
    };

    const changeValidators = (isPublish: boolean) => {
        const newValidators = isPublish ? [requiredValidator] : [];
        const channelValidators =
            isPublish && !form.isUserGuideArticle.value
                ? [requiredValidator]
                : [];

        form.articleText.setValidators(newValidators);
        form.publisher.setValidators(channelValidators);
        form.contributors.setValidators(newValidators);
        form.detailSummary.setValidators(newValidators);
        form.categories.setValidators(newValidators);
        form.publishDate.setValidators([dateValidator]);
        form.channel.setValidators(channelValidators);
    };

    const deleteArticle = () => {
        setPageMessage({ style: 'info', message: 'deleted' });
    };

    const saveArticle = async (item: any): Promise<void> => {
        if (duplicateTabBlocked) {
            return;
        }

        try {
            setBusy(true);

            editMode
                ? await editDbAction({ variables: { item } })
                : await addDbAction({ variables: { item } });

            if (!editMode) {
                localStorage.setItem(
                    NEW_ARTICLE_CREATED_KEY,
                    Date.now().toString()
                );
            }

            showMessage(
                generateSnackBarFormMessage(
                    ManageTemplateInstance.Article,
                    form.articleTitle.value,
                    editMode ? FormSnackBarType.Updated : FormSnackBarType.Added
                )
            );
            clearArticleDraftStorage(_id);
            goBack();
        } catch (error) {
            console.error(error.message);
            setBusy(false);
            setPageMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const publishArticle = async (item: any): Promise<void> => {
        if (duplicateTabBlocked) {
            return;
        }

        try {
            setBusy(true);

            editMode
                ? await publishEditDbAction({ variables: { item } })
                : await publishAddDbAction({ variables: { item } });

            if (!editMode) {
                localStorage.setItem(
                    NEW_ARTICLE_CREATED_KEY,
                    Date.now().toString()
                );
            }

            showMessage(
                generateSnackBarFormMessage(
                    ManageTemplateInstance.Article,
                    form.articleTitle.value,
                    FormSnackBarType.Published
                )
            );
            clearArticleDraftStorage(_id);
            goBack();
        } catch (error) {
            console.error(error.message);
            setBusy(false);
            setPageMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const archiveArticle = async (): Promise<void> => {
        if (!editMode) {
            return;
        }
        try {
            setBusy(true);

            await archiveDbAction({
                variables: {
                    item: {
                        _id,
                        version: data?.document?.version,
                    },
                },
            });

            showMessage(
                generateSnackBarFormMessage(
                    ManageTemplateInstance.Article,
                    form.articleTitle.value,
                    FormSnackBarType.Archived
                )
            );
            clearArticleDraftStorage(_id);
            goBack();
        } catch (error) {
            console.error(error.message);
            setBusy(false);
            setPageMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const isNotFreeOpenChannel =
        selectedChannel && selectedChannel.type !== ChannelTypes.freeOpen;

    const generateArticleDto = useCallback(
        (withEmail?: boolean): ArticleUpsertDto => {
            // promotionalContent parser
            const html = htmlToPromotionalContentShortcodes(
                form.articleText.value || ''
            );
            const publisherId =
                form.publisher.value ||
                (form.isUserGuideArticle.value ? userDetails?.id : undefined);
            const publisherType = form.isUserGuideArticle.value
                ? ChannelOwner.contributor
                : (preparedChannelsList.find(
                      (channel) => channel._id === form.channel.value
                  )?.ownerType ?? ChannelOwner.desk);

            return {
                _id,
                publishedBy: publisherId
                    ? {
                          _id: publisherId,
                          type: publisherType,
                      }
                    : null,
                contributors: form.contributors.value ?? [],
                author: userDetails?.id,
                title: form.articleTitle.value || '',
                html: html,
                summary: form.detailSummary.value || '',
                categories: form.categories.value ? form.categories.value : [],
                tags: form.tags.value ? form.tags.value : [],
                state: ArticleState[ArticleState.Pending],
                version: data?.document?.version,
                sendEmails: withEmail,
                publishDate: form.publishDate.value || undefined,
                templateId:
                    data?.document?.templateId ||
                    templateQuery?.data?.document?._id,
                isPremium: isNotFreeOpenChannel
                    ? formatIsPremiumValue(form.openToAll.value)
                    : undefined,
                promotionalContentIds: getPromotionalContentIdsFromHtml(html),
                premiumExpiryOffset: form.expiryOffset.value || '',
                premiumChannel: form.channel.value ? form.channel.value : null,
                showWatermark: form.showWatermark.value,
                displayInChartSection: form.displayInChartSection.value,
                isPinned: form.isPinned.value,
                isTimeless: form.isTimeless.value,
                isUserGuideArticle: form.isUserGuideArticle.value,
                userGuideScopes: form.isUserGuideArticle.value
                    ? expandSelectedUserGuideScopeValuesToStoredScopes(
                          form.userGuideScopes.value
                      )
                    : [],
            };
        },
        [
            _id,
            data?.document?.templateId,
            data?.document?.version,
            form.articleText.value,
            form.categories.value,
            form.channel.value,
            form.contributors.value,
            form.detailSummary.value,
            form.displayInChartSection.value,
            form.expiryOffset.value,
            form.userGuideScopes.value,
            form.isUserGuideArticle.value,
            form.isPinned.value,
            form.isTimeless.value,
            form.openToAll.value,
            form.publishDate.value,
            form.publisher.value,
            form.showWatermark.value,
            form.tags.value,
            form.articleTitle.value,
            isNotFreeOpenChannel,
            preparedChannelsList,
            templateQuery?.data?.document?._id,
            userDetails?.id,
        ]
    );

    const isArticlePageReady =
        !loading &&
        !staticLoading &&
        !templateQuery.loading &&
        !channelsLoading &&
        !desksLoading &&
        !allChannelsLoading;

    const { articleEditorKey, draftHandlers, markInitialArticleValuesReady } =
        useArticleDraftAutoSave({
            articleId: _id,
            templateId,
            isPageReady: isArticlePageReady,
            generateArticleDto,
            setInitialFormValues,
        });

    useEffect(() => {
        if (templateId && templateQuery.data?.document) {
            markInitialArticleValuesReady();
        }
    }, [markInitialArticleValuesReady, templateId, templateQuery.data]);

    useEffect(() => {
        const autoSaveBanner = localStorage?.getItem('autoSaveMessage');
        const isAutoSaveBannerHidden = autoSaveBanner
            ? Boolean(autoSaveBanner)
            : false;
        if (isAutoSaveBannerHidden) return;

        setPageMessage({
            style: 'info',
            message: (
                <>
                    <b>Insights will auto save your content as you type.</b> If
                    you have any issues with this screen, you can go back and
                    resume editing.
                </>
            ),
            id: 'autoSaveMessage',
        });
    }, []);

    useEffect(() => {
        // Only relevant while this tab is still a brand-new, unsaved
        // article: once it has an _id (editMode), server-side version
        // checking already prevents duplicate writes.
        if (editMode) {
            return;
        }

        const handleNewArticleCreatedElsewhere = (event: StorageEvent) => {
            if (event.key !== NEW_ARTICLE_CREATED_KEY || !event.newValue) {
                return;
            }

            setDuplicateTabBlocked(true);
            setPageMessage({
                style: 'error',
                message:
                    'This article was just created from another tab or window. To avoid creating a duplicate, please close this tab instead of saving or publishing again.',
            });
        };

        window.addEventListener('storage', handleNewArticleCreatedElsewhere);
        return () =>
            window.removeEventListener(
                'storage',
                handleNewArticleCreatedElsewhere
            );
    }, [editMode]);

    useEffect(() => {
        getChannels();
    }, [getChannels]);

    useEffect(() => {
        if (data?.document && staticData?.promotionalContent) {
            const article: Article = {
                ...data.document,
                html: promotionalContentShortcodesToHtml(
                    data.document.html,
                    staticData.promotionalContent
                ),
            };
            setInitialFormValues(article);
            markInitialArticleValuesReady();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, staticData]);

    useEffect(() => {
        let users: User[] = [];

        if (desksData) {
            if (
                isAdmin ||
                channelFormControl.value ||
                isUserGuideArticleFormControl.value
            ) {
                const currentChannel = preparedChannelsList.find(
                    (i: Channel) => i._id === channelFormControl.value
                );

                if (currentChannel && currentChannel.ownerType === 'desk') {
                    const currentDesk = staticData?.desks.find(
                        (i: Desk) => i._id === currentChannel.ownerId
                    );

                    users = getContributorRoleUsers(
                        getUsersWithNames(currentDesk?.contributors)
                    );
                } else {
                    users = getContributorRoleUsers(
                        getUsersWithNames(staticData?.users)
                    );
                }
            }
        }

        setListUsers(users);
    }, [
        isAdmin,
        channelFormControl.value,
        isUserGuideArticleFormControl.value,
        publisherFormControl.value,
        preparedChannelsList,
        staticData,
        desksData,
    ]);

    useEffect(() => {
        if (!data || !allChannelsData || !desksData) return;
        // close this page for not admins, not contributors and not desk contributors

        const desksContributors =
            data.document.publishedBy?.type === 'desk'
                ? desksData?.documents.find(
                      (d: Desk) => d._id === data.document.publishedBy?._id
                  )?.contributors
                : [];

        const channelAdmins = data.document.premiumChannel
            ? (allChannelsData?.channels?.find(
                  (channel) => channel._id === data.document.premiumChannel
              )?.adminUsers ?? [])
            : [];

        if (!(
            isAdmin ||
            usersContains(channelAdmins, userDetails?.id || '') ||
            usersContains(
                data.document.contributors || [],
                userDetails?.id || ''
            ) ||
            usersContains(desksContributors, userDetails?.id || '')
        )) {
            history.push(baseUrls.publisher.articles.list);
        }
    }, [allChannelsData, data, desksData, history, isAdmin, userDetails?.id]);

    if (
        loading ||
        staticLoading ||
        templateQuery.loading ||
        channelsLoading ||
        desksLoading ||
        allChannelsLoading
    )
        return <Loader />;

    const subscribersCount =
        querySubscribers.data?.getSubscribersCount?.count ?? 0;

    const treeCategories =
        (staticData?.categoriesAsTree as CategoryItemNode[]) ?? [];
    const categoryList = getCategoriesAsList(treeCategories, []);
    const categories = form.categories.value.map((categoryId) =>
        categoryList.find((category: any) => category._id === categoryId)
    );
    const categoriesToString = categories
        .map((category) => category?.name)
        .join(', ');
    const errorList = validateBeforePublish();
    const textAbsentError = errorList.find(
        (x) => x.errorType === ArticleEditError.TextAbsent
    )?.message;
    const headerTitleError = form.articleTitle.showError
        ? form.articleTitle.validationMessage
        : '';

    return (
        <Box component="form" onSubmit={onSubmit} sx={{ height: '100%' }}>
            <Box sx={{ display: 'flex', width: '100%', height: '100%', pl: 2 }}>
                <ContainerBox sx={{ px: 0 }}>
                    <Box sx={{ maxWidth: 1200, width: '100%' }}>
                        <AddEditHeader
                            isNew={!editMode}
                            editableHeader={true}
                            editablePlaceholder="Type to edit the title..."
                            entityName="article"
                            pathArea="Articles"
                            returnUrl={returnListUrl}
                            value={form.articleTitle.value}
                            errorMessage={headerTitleError}
                            onChange={(articleTitle: string) => {
                                const articleTitleWithoutLineBreaks =
                                    articleTitle.replace(/(\r\n|\n|\r)/gm, ' ');
                                form.articleTitle.setValue(
                                    articleTitleWithoutLineBreaks
                                );
                            }}
                            isResumeMessageVisible={draftHandlers}
                        />
                        <Box sx={{ my: 2 }}>
                            <AlertBox
                                id="article-add-edit-alert"
                                visible={!!pageMessage.message}
                                children={pageMessage.message}
                                styleName={pageMessage.style}
                                onClose={() => {
                                    if (pageMessage.id === 'autoSaveMessage') {
                                        localStorage.setItem(
                                            'autoSaveMessage',
                                            'true'
                                        );
                                    }
                                    setPageMessage({
                                        style: 'info',
                                        message: null,
                                    });
                                }}
                            />
                        </Box>
                        {
                            // Froala bug. Can't send multiple updates in quick succession.
                            // See https://github.com/froala/react-froala-wysiwyg/issues/221#issuecomment-605609436
                        }
                        {(form.articleText.value !== undefined ||
                            (!editMode && !templateId)) && (
                            <ArticleEditor
                                key={articleEditorKey}
                                initialHtml={form.articleText.value ?? ' '}
                                errorMessage={textAbsentError}
                                onChange={(html) => {
                                    form.articleText.setValue(html);
                                }}
                                promotionalContent={
                                    listPromotionalContent?.sort(
                                        (a: any, b: any) =>
                                            a.name
                                                .toLowerCase()
                                                .localeCompare(
                                                    b.name.toLowerCase()
                                                )
                                    ) || []
                                }
                                setPageMessage={setPageMessage}
                            />
                        )}
                    </Box>
                </ContainerBox>

                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderLeft: 1,
                        borderColor: 'divider',
                        ml: 2,
                        minWidth: 368,
                        width: 368,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <ArticleSideBar
                        isNew={!editMode}
                        selectedContributors={form.contributors.value}
                        detailSummary={form.detailSummary.value}
                        publishDate={form.publishDate.value}
                        listUsers={listUsers}
                        treeCategories={treeCategories}
                        listTags={listTags}
                        preparedChannelsList={preparedChannelsList}
                        selectedCategories={form.categories.value}
                        selectedTags={form.tags.value}
                        openToAll={form.openToAll.value}
                        expiryOffset={form.expiryOffset.value ?? ''}
                        selectedChannel={selectedChannel}
                        showWatermark={form.showWatermark.value}
                        displayInChartSection={form.displayInChartSection.value}
                        isPinned={form.isPinned.value}
                        isTimeless={form.isTimeless.value}
                        isUserGuideArticle={form.isUserGuideArticle.value}
                        userGuideScopes={form.userGuideScopes.value}
                        availableUserGuideScopes={
                            availableUserGuideScopeOptions
                        }
                        userHasTableuAccess={userHasTableuAccess}
                        formBusy={formBusy || duplicateTabBlocked}
                        articleInfo={articleInfo}
                        errorList={errorList}
                        archiveArticle={() => setConfirmationDialogOpened(true)}
                        publishArticle={() => {
                            handleValidation();
                            if (form.getIsValid()) {
                                setPublishDialogOpened(true);
                            }
                        }}
                        deleteDraft={deleteArticle}
                        onChangeValue={onChangeSidebarValue}
                        onChangeMultiValue={onChangeMultiValue}
                    />

                    <ManageTemplateDialog
                        title="Archive this article?"
                        dialogMessage={
                            <>
                                You are about to archive the article
                                <strong> {form.articleTitle?.value}</strong>.
                                Archived articles are removed from publication
                                so you must be sure and confirm.
                            </>
                        }
                        submitBtnText="Archive article"
                        dialogOpenState={confirmationDialogOpened}
                        onDelete={archiveArticle}
                        submitBtnColorType="error"
                        closeDialog={() => setConfirmationDialogOpened(false)}
                    />

                    <PublishDialog
                        dialogOpenState={publishDialogOpened}
                        articleTitle={form.articleTitle?.value}
                        onPublish={onPublish}
                        onCloseDialog={() => setPublishDialogOpened(false)}
                        articleState={data?.document?.state}
                        wasPublishedPreviously={wasPublishedPreviously}
                        isCustomPublishDate={!!form.publishDate.value}
                        categoriesAsString={categoriesToString}
                        numberOfSubscribers={subscribersCount}
                    />
                </Box>
            </Box>
        </Box>
    );
};

interface PublishDialogProps {
    dialogOpenState: boolean;
    onCloseDialog: () => void;
    articleTitle: string;
    onPublish: (isSendEmails: boolean) => void;
    articleState?: ArticleState;
    wasPublishedPreviously: boolean;
    isCustomPublishDate: boolean;
    numberOfSubscribers?: number;
    categoriesAsString?: string;
}

const PublishDialog: React.FC<PublishDialogProps> = ({
    dialogOpenState,
    onCloseDialog,
    articleTitle,
    onPublish,
    articleState,
    wasPublishedPreviously,
    isCustomPublishDate,
    numberOfSubscribers = 0,
    categoriesAsString = '',
}) => {
    const [isSendEmails, setIsSendEmails] = useState<boolean>(false);

    const publishOrUpdate =
        articleState === ArticleState.Published ? 'Update' : 'Publish';
    const showCheckbox =
        articleState === ArticleState.Published ||
        wasPublishedPreviously ||
        isCustomPublishDate;
    const checkboxLabel = `Notify
	${numberOfSubscribers > 1 ? `${numberOfSubscribers} subscribers` : `${numberOfSubscribers} subscriber`}`;
    const labelType = getDialogType(
        publishOrUpdate,
        numberOfSubscribers,
        isCustomPublishDate
    );
    const labels = getLabelAccordingToDialogType(labelType);

    const body = (
        <Box>
            <Typography
                variant="body1"
                sx={{
                    color: 'text.secondary',
                }}
            >
                You are about to {publishOrUpdate.toLowerCase()} the article{' '}
                <strong>{articleTitle}</strong>.{labels.notification}
                {labels.confirmation}
            </Typography>
            <Box>
                {publishOrUpdate === 'Publish' &&
                    !isCustomPublishDate &&
                    numberOfSubscribers > 0 && (
                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                p: 2,
                                mt: 2,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                    mb: 0.5,
                                }}
                            >
                                Number of category subscribers
                            </Typography>
                            <Typography variant="body1">
                                {`${numberOfSubscribers} ${numberOfSubscribers > 1 ? 'users' : 'user'}`}
                            </Typography>
                        </Box>
                    )}
            </Box>
            {showCheckbox && numberOfSubscribers > 0 && (
                <Box sx={{ mt: 2, '& > label': { mb: 0 } }}>
                    <Checkbox
                        id="notify-subscribers-checkbox"
                        label={checkboxLabel}
                        value="a"
                        checked={isSendEmails}
                        onChange={(e) => setIsSendEmails(e?.target?.checked)}
                    />
                </Box>
            )}
            {publishOrUpdate === 'Publish' && (
                <Typography sx={{ mt: 2 }} variant="body1">
                    This article will be published under{' '}
                    <strong>{categoriesAsString}</strong>.
                </Typography>
            )}
        </Box>
    );

    return (
        <ManageTemplateDialog
            title={publishOrUpdate + ' this article?'}
            submitBtnText={publishOrUpdate + ' article'}
            dialogOpenState={dialogOpenState}
            onDelete={() => onPublish(!showCheckbox || isSendEmails)}
            submitBtnColorType="primary"
            closeDialog={onCloseDialog}
        >
            {body}
        </ManageTemplateDialog>
    );
};

export default ArticleAddEdit;
